import { __extends } from "tslib";
import { RuleSeverity, ValidationState } from './ValidationState';
import { Utils } from '../Utils';
export var AsyncStage;
(function (AsyncStage) {
    AsyncStage[AsyncStage["None"] = 0] = "None";
    AsyncStage[AsyncStage["Debounce"] = 1] = "Debounce";
    AsyncStage[AsyncStage["Busy"] = 2] = "Busy";
    AsyncStage[AsyncStage["Completing"] = 3] = "Completing";
})(AsyncStage || (AsyncStage = {}));
var RuleContextBase = /** @class */ (function (_super) {
    __extends(RuleContextBase, _super);
    function RuleContextBase(ruleValidator) {
        var _this = _super.call(this) || this;
        _this.ruleValidator = ruleValidator;
        _this.asyncBusyText = "";
        _this.errors = [];
        _this.warnings = [];
        _this.messages = [];
        return _this;
    }
    RuleContextBase.prototype.addError = function (errorText, forceDisplay) {
        if (forceDisplay === void 0) { forceDisplay = false; }
        this.errors.push(errorText);
        this.severity = RuleSeverity.Error;
        if (forceDisplay)
            this.forceDisplaySeverity = RuleSeverity.Error;
    };
    RuleContextBase.prototype.addWarning = function (warningText, forceDisplay) {
        if (forceDisplay === void 0) { forceDisplay = false; }
        this.warnings.push(warningText);
        if (this.severity < RuleSeverity.Warning)
            this.severity = RuleSeverity.Warning;
        if (forceDisplay && this.forceDisplaySeverity < RuleSeverity.Warning)
            this.forceDisplaySeverity = RuleSeverity.Warning;
    };
    RuleContextBase.prototype.addMessage = function (message, forceDisplay) {
        if (forceDisplay === void 0) { forceDisplay = false; }
        this.messages.push(message);
        if (this.severity < RuleSeverity.Info)
            this.severity = RuleSeverity.Info;
        if (forceDisplay && this.forceDisplaySeverity < RuleSeverity.Info)
            this.forceDisplaySeverity = RuleSeverity.Info;
    };
    RuleContextBase.prototype.merge = function (ruleState, includeMessages) {
        var _a, _b, _c;
        if (includeMessages) {
            (_a = this.errors).push.apply(_a, ruleState.errors);
            (_b = this.warnings).push.apply(_b, ruleState.warnings);
            (_c = this.messages).push.apply(_c, ruleState.messages);
        }
        this.severity = Math.max(this.severity, ruleState.severity);
        this.forceDisplaySeverity = Math.max(this.forceDisplaySeverity, ruleState.forceDisplaySeverity);
    };
    Object.defineProperty(RuleContextBase.prototype, "hasDetail", {
        get: function () {
            return this.messages.length > 0 || this.warnings.length > 0 || this.errors.length > 0 || this.asyncBusyText.length > 0;
        },
        enumerable: false,
        configurable: true
    });
    return RuleContextBase;
}(ValidationState));
export { RuleContextBase };
var RuleContext = /** @class */ (function (_super) {
    __extends(RuleContext, _super);
    function RuleContext() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    /**
     * Indicates that this object is validating.
     * It will not be valid until endAsync is called in the provided callback.
     * Async / await, or returning a promise can also be used as an alternative to using endAsync.
     * @param loadingText Text to display until endAsync is called.
     * @param onReady Callback function where the async action must be called from.
     * @param options See options properties.
     */
    RuleContext.prototype.beginAsync = function (loadingText, onReady, options) {
        var _this = this;
        var model = this.ruleValidator.propertyInstance.attachedTo;
        if (!model.isSuspended && ((options && options.checkWhenClean) || model.meta.isSelfDirty.peek) && this.ruleValidator.asyncStage !== AsyncStage.Completing) {
            this.asyncBusyText = loadingText;
            this.ruleValidator.asyncStage = AsyncStage.Debounce;
            if (this.severity < RuleSeverity.AsyncBusy)
                this.severity = RuleSeverity.AsyncBusy;
            if (options && options.forceDisplay && this.forceDisplaySeverity < RuleSeverity.AsyncBusy)
                this.forceDisplaySeverity = RuleSeverity.AsyncBusy;
            if (this.ruleValidator.debounce)
                clearTimeout(this.ruleValidator.debounce);
            this.ruleValidator.debounce = setTimeout(function () {
                _this.ruleValidator.asyncContext = _this;
                _this.ruleValidator.asyncStage = AsyncStage.Busy;
                var asyncContext = new AsyncRuleContext(_this, _this.ruleValidator);
                var promise = onReady(asyncContext);
                if (promise) {
                    promise.catch(function (e) { return asyncContext.addError("Error: " + e, true); });
                    promise.finally(function () {
                        asyncContext.endAsync();
                    });
                }
            }, ((options === null || options === void 0 ? void 0 : options.delayMs) !== undefined ? options.delayMs : 500));
        }
    };
    return RuleContext;
}(RuleContextBase));
export { RuleContext };
var AsyncRuleContext = /** @class */ (function (_super) {
    __extends(AsyncRuleContext, _super);
    function AsyncRuleContext(initiator, ruleValidator) {
        var _this = _super.call(this, ruleValidator) || this;
        _this.initiator = initiator;
        return _this;
    }
    /**
     * Indicates that the async operation has completed.
     * This does not need to be called if the method returns a promise (or uses the async keyword).
     */
    AsyncRuleContext.prototype.endAsync = function () {
        var _this = this;
        if (this.ruleValidator.asyncContext && this.ruleValidator.asyncContext === this.initiator && this.ruleValidator.asyncStage === AsyncStage.Busy) {
            this.ruleValidator.asyncContext = this;
            this.ruleValidator.asyncStage = AsyncStage.Completing;
            // cause observers to refresh 
            this.ruleValidator.refreshValue += 1;
            // allow beginAsync to run next time
            Utils.nextTick(function () {
                _this.ruleValidator.asyncContext = null;
                _this.ruleValidator.asyncStage = AsyncStage.None;
            });
        }
    };
    return AsyncRuleContext;
}(RuleContextBase));
export { AsyncRuleContext };
